/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `apiErrorMessages` for the Caintra Connect frontend.
 * @module utils/apiErrorMessages
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import i18n from "../i18n";

const API_ERROR_KEY_PREFIX = "errors.api";

/**
 * Maps API error codes to user-facing messages via i18n translation files.
 * For logging caught exceptions, use `logger.error(message, { err: sanitizeError(error) })`
 * from `./logger.js`.
 *
 * @param {{ errorType?: string, code?: string } | null | undefined} error
 * @returns {string}
 */
export const getApiErrorMessage = (error) => {
  const errorType = error?.errorType ?? error?.code;
  if (!errorType) {
    return i18n.t(`${API_ERROR_KEY_PREFIX}.UNEXPECTED_ERROR`);
  }

  const translationKey = `${API_ERROR_KEY_PREFIX}.${errorType}`;
  if (i18n.exists(translationKey)) {
    return i18n.t(translationKey);
  }

  return i18n.t(`${API_ERROR_KEY_PREFIX}.UNKNOWN_ERROR`);
};
